import apiClient from './api.service';
import {
  PublicAuditTrailEntriesResult,
  PublicAuditTrailEntryDetail,
  PublicAuditTrailSummary,
  QuantitativeSummaryResponse,
} from '../types/audit-trail.types';

class AuditTrailService {
  async getSummary(surveyHash: string): Promise<PublicAuditTrailSummary> {
    const response = await apiClient.get<{ data: PublicAuditTrailSummary }>(
      `/api/public/surveys/${surveyHash}/audit-trail`
    );
    return response.data.data;
  }

  async listEntries(
    surveyHash: string,
    page: number = 1,
    pageSize: number = 50,
    hashSearch?: string
  ): Promise<PublicAuditTrailEntriesResult> {
    const response = await apiClient.get<{
      data: PublicAuditTrailEntriesResult['items'];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    }>(`/api/public/surveys/${surveyHash}/audit-trail/entries`, {
      params: { page, pageSize, ...(hashSearch ? { hashSearch } : {}) },
    });

    return {
      items: response.data.data,
      page: response.data.pagination.page,
      pageSize: response.data.pagination.limit,
      total: response.data.pagination.total,
      totalPages: response.data.pagination.totalPages,
    };
  }

  async getEntryDetail(
    surveyHash: string,
    responseHash: string
  ): Promise<PublicAuditTrailEntryDetail> {
    const response = await apiClient.get<{ data: PublicAuditTrailEntryDetail }>(
      `/api/public/surveys/${surveyHash}/audit-trail/entries/${responseHash}/detail`
    );
    return response.data.data;
  }

  async getQuantitativeSummary(surveyHash: string): Promise<QuantitativeSummaryResponse> {
    const response = await apiClient.get<{ data: QuantitativeSummaryResponse }>(
      `/api/public/surveys/${surveyHash}/quantitative-summary`
    );
    return response.data.data;
  }

  async getComplementaryAttachmentDownloadUrl(surveyId: number): Promise<string | null> {
    try {
      const response = await apiClient.get<{ url: string }>(
        `/api/surveys/${surveyId}/analysis/complementary/attachment/download-url`
      );
      return response.data?.url || null;
    } catch {
      return null;
    }
  }

  async downloadComplementaryAttachment(surveyId: number): Promise<Blob> {
    const response = await apiClient.get(
      `/api/surveys/${surveyId}/analysis/complementary/attachment/download`,
      { responseType: 'blob' }
    );
    return response.data as Blob;
  }
}

export const auditTrailService = new AuditTrailService();
